import { useState, type FormEvent } from "react";
import type { ProjectMember, ProjectRole } from "../types/domain";

const roleLabels: Record<ProjectRole, string> = {
  owner: "Owner",
  editor: "Editor",
  viewer: "Viewer",
};

export function MembersView({
  members,
  currentUserId,
  currentRole,
  onAdd,
  onRoleChange,
  onRemove,
}: {
  members: ProjectMember[];
  currentUserId: string;
  currentRole: ProjectRole | null;
  onAdd(handle: string, role: ProjectRole): Promise<void>;
  onRoleChange(userId: string, role: ProjectRole): Promise<void>;
  onRemove(userId: string): Promise<void>;
}) {
  const isOwner = currentRole === "owner";
  const [handle, setHandle] = useState("");
  const [role, setRole] = useState<ProjectRole>("viewer");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await onAdd(handle, role);
      setHandle("");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="view-stack">
      <section className="section-toolbar">
        <div>
          <span className="eyebrow">Colaboración controlada</span>
          <h2>Miembros</h2>
          <p>El frontend muestra controles por rol; RLS vuelve a validar cada operación.</p>
        </div>
      </section>
      {isOwner && (
        <section className="panel">
          <header className="panel__header">
            <div>
              <span className="eyebrow">Añadir cuenta existente</span>
              <h3>Compartir proyecto</h3>
            </div>
          </header>
          <form className="inline-form" onSubmit={(event) => void submit(event)}>
            <label>
              Handle
              <input
                required
                placeholder="usuario"
                value={handle}
                onChange={(event) => setHandle(event.target.value.toLowerCase())}
              />
            </label>
            <label>
              Rol
              <select value={role} onChange={(event) => setRole(event.target.value as ProjectRole)}>
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="owner">Owner</option>
              </select>
            </label>
            <button className="button button--primary" disabled={submitting} type="submit">
              {submitting ? "Añadiendo…" : "Añadir"}
            </button>
          </form>
          <p className="muted small-text">
            La cuenta debe registrarse primero y conocer su handle. El MVP no usa service_role ni
            invitaciones administrativas desde el navegador.
          </p>
        </section>
      )}
      <section className="panel members-panel">
        {members.map((member) => (
          <article className="member-row" key={member.userId}>
            <div className="avatar">{member.profile.displayName.slice(0, 1).toUpperCase()}</div>
            <div className="member-row__identity">
              <strong>
                {member.profile.displayName}
                {member.userId === currentUserId && " · tú"}
              </strong>
              <span>@{member.profile.handle}</span>
            </div>
            {isOwner && member.userId !== currentUserId ? (
              <>
                <select
                  value={member.role}
                  onChange={(event) =>
                    void onRoleChange(member.userId, event.target.value as ProjectRole)
                  }
                >
                  {Object.entries(roleLabels).map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <button
                  className="button button--danger button--small"
                  type="button"
                  onClick={() => void onRemove(member.userId)}
                >
                  Retirar
                </button>
              </>
            ) : (
              <span className={`role-pill role-pill--${member.role}`}>
                {roleLabels[member.role]}
              </span>
            )}
          </article>
        ))}
      </section>
    </div>
  );
}
